/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * resultsApi — reading and writing the motion board's tables.
 *
 * Phase 2 uses the write side (attaching a run to a race). Phase 3 reuses the
 * read side for the leaderboard itself.
 *
 * The time, distance and finish are COPIED onto event_result rather than read
 * through the activity each time, so editing or deleting the activity later
 * cannot change an official result. That is the design doc's rule, and it is
 * why resultPayloadFor takes a whole activity and flattens it.
 *
 * Validation is NOT done here. A check that runs only in the browser is not a
 * check — the server's record.before_created hook refuses a bad attach and
 * returns its reason as {"error": "..."} with a 400, which readError surfaces
 * verbatim so the person reads the real sentence rather than a generic failure.
 */

import envConfig from '../../../config/envConfig';
import { idOf } from '../feedApi';

export const RESULT_TABLE = 'event_result';
export const TIMING_SOURCE_TABLE = 'event_timing_source';
export const REGISTRATION_TABLE = 'event_registration';

/** A run recorded in our own app, as opposed to an organizer's import. */
export const APPLICATION_TRACKED = 'APPLICATION_TRACKED';

export const REGISTRATION_PAGE_SIZE = 100;

function scope() {
  return {
    organization_id: Number(envConfig.organizationId),
    application_id: Number(envConfig.applicationId),
  };
}

/** The server's message when it refuses, or `fallback`. */
export async function readError(resp, fallback) {
  try {
    const body = await resp.json();
    const message = body && (body.error || body.detail || body.message);
    if (message) return Array.isArray(message) ? message.join(' ') : String(message);
  } catch (e) { /* fall through to the generic message */ }
  return fallback;
}

async function fetchRows(api, body, { signal, errorMessage } = {}) {
  const resp = await api.apiCall('/arcee/application/table/fetch/', {
    method: 'POST',
    signal,
    body: JSON.stringify({ ...scope(), ...body }),
  });
  if (!resp.ok) throw new Error(errorMessage || 'Could not load your results.');
  const payload = await resp.json();
  const rows = Array.isArray(payload) ? payload : (payload?.records || payload?.results || []);
  const count = typeof payload?.count === 'number' ? payload.count : rows.length;
  return { rows, count };
}

/** The three timing-source rows. Seeded by the pack, so this is a small read. */
export async function fetchTimingSources(api, { signal } = {}) {
  const { rows } = await fetchRows(api, { table_name: TIMING_SOURCE_TABLE, page: 1, page_size: 50 },
    { signal, errorMessage: 'Could not load the timing sources.' });
  return rows;
}

/** The id of the timing source with this code, or null. */
export function timingSourceIdByCode(rows, code) {
  const match = (rows || []).find((r) => String(r.code || '').toUpperCase() === code);
  return match ? match.id : null;
}

// ── Commented out 2026-11-02, phase 7a ─────────────────────────────────────
// Dead code: nothing outside this file's own tests ever called these four.
// fetchMyRegistrations here also duplicated a widely-used name at
// components/athlohive/event-detail/fetchMyRegistrations.js, which is the one
// EventDetailPage uses and is untouched. Kept commented rather than deleted in
// case athlete attach comes back in a later phase.
// /** Every race this user is registered for. */
// export async function fetchMyRegistrations(api, { userId, signal } = {}) {
//   if (userId === null || userId === undefined) return { rows: [], count: 0 };
//   return fetchRows(api, {
//     table_name: REGISTRATION_TABLE,
//     filters: [{ column_key: 'created_by_id', filter_type: 'equals', column_value: userId }],
//     ordering: '-registered_at',
//     page: 1,
//     page_size: REGISTRATION_PAGE_SIZE,
//   }, { signal, errorMessage: 'Could not load the races you have entered.' });
// }
//
// /** The results this user already has, so a race is not offered twice. */
// export async function fetchMyResults(api, { userId, signal } = {}) {
//   if (userId === null || userId === undefined) return { rows: [], count: 0 };
//   return fetchRows(api, {
//     table_name: RESULT_TABLE,
//     filters: [{ column_key: 'created_by_id', filter_type: 'equals', column_value: userId }],
//     page: 1,
//     page_size: REGISTRATION_PAGE_SIZE,
//   }, { signal, errorMessage: 'Could not load your results.' });
// }
//
// /** Registration ids that already carry a result. */
// export function attachedRegistrationIds(resultRows) {
//   const ids = new Set();
//   (resultRows || []).forEach((row) => {
//     const id = idOf(row.registration_id);
//     if (id !== undefined && id !== null) ids.add(String(id));
//   });
//   return ids;
// }

/**
 * The event_result row for attaching `activity` to `registrationId`.
 *
 * Only the figures the board needs are copied. Position is left empty on
 * purpose: it is worked out at publish time, not when a run arrives — see the
 * phase plan for why that slot is deliberately avoided.
 */
export function resultPayloadFor({
  registrationId, activity, timingSourceId, bib, eventActivityId, athleteName,
}) {
  const row = activity || {};
  const duration = Number(row.duration_seconds);
  const distance = Number(row.distance_meters);
  const payload = {
    registration_id: registrationId,
    activity_id: row.id,
    timing_source_id: timingSourceId,
    status: 'FINISHED',
  };
  // The two columns the board reads by. A filter only applies to a table's own
  // columns, so without the race here the board would have to fetch every
  // registration of the race first; without the name it could not search.
  // The name is a frozen copy — a results list keeps saying what it said on the day.
  if (eventActivityId !== undefined && eventActivityId !== null) {
    payload.event_activity_id = eventActivityId;
  }
  if (athleteName) payload.athlete_name = String(athleteName).slice(0, 128);
  if (row.started_at) payload.started_at = row.started_at;
  if (row.ended_at) payload.finished_at = row.ended_at;
  if (Number.isFinite(duration) && duration >= 0) payload.duration_seconds = Math.round(duration);
  if (Number.isFinite(distance) && distance >= 0) payload.distance_meters = distance;
  if (bib) payload.bib = String(bib);
  return payload;
}

/**
 * Attaches a run to a race. Throws with the server's own sentence when the
 * validation hook refuses — "A Drive cannot be attached to a Run race", not
 * "request failed".
 */
export async function attachRunToRace(api, {
  registrationId, activity, timingSourceId, bib, eventActivityId, athleteName,
}) {
  const payload = resultPayloadFor({
    registrationId, activity, timingSourceId, bib, eventActivityId, athleteName,
  });
  const resp = await api.apiCall('/arcee/application/table/save/record/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...scope(), table_name: RESULT_TABLE, payload }),
  });
  if (!resp.ok) throw new Error(await readError(resp, 'Could not add this run to the race.'));
  return resp.json().catch(() => ({}));
}

/**
 * A foreign key's human label. table/fetch expands a fk as
 * { related_table_name, record_identifier, data: { … } }, so the readable name
 * is on the wrapper — but other paths send a bare id, hence the fallback.
 */
export function labelOf(value, fallback = '') {
  if (!value || typeof value !== 'object') return fallback;
  const data = value.data || {};
  // `record_identifier` is the NAME of the column that identifies the row —
  // "title" for an event — not its value. Reading it directly printed the word
  // "title" under every race on the organizer's Results page.
  const key = value.record_identifier;
  // First non-blank wins. Blank-but-present is treated as missing: a title of
  // spaces is no more use on screen than no title at all.
  const candidates = [key ? data[key] : null, data.name, data.title, data.display_name];
  const found = candidates.find((v) => v !== undefined && v !== null && String(v).trim());
  return found !== undefined ? String(found).trim() : fallback;
}

// ── Commented out 2026-11-02, phase 7a ─────────────────────────────────────
// Dead code: nothing outside this file's own tests ever called these four.
// fetchMyRegistrations here also duplicated a widely-used name at
// components/athlohive/event-detail/fetchMyRegistrations.js, which is the one
// EventDetailPage uses and is untouched. Kept commented rather than deleted in
// case athlete attach comes back in a later phase.
// /** "Half Marathon · Bengaluru Midnight Half" for one registration. */
// export function raceLabelFor(registration) {
//   const row = registration || {};
//   const race = labelOf(row.event_activity_id, '');
//   const event = labelOf(row.event_id, '');
//   if (race && event) return `${race} · ${event}`;
//   return race || event || 'Your race';
// }

/** The saved query listing races the caller has entered but not yet added a run to. */
export const OPEN_RACES_QUERY = 'athlete_open_races';

/** An analytics answer ({columns, rows}) as objects keyed by column name. */
function asRecords(payload) {
  const columns = (payload?.columns || []).map((c) => c.name);
  return (payload?.rows || []).map((row) => Object.fromEntries(columns.map((c, i) => [c, row[i]])));
}

/**
 * The races this athlete has entered that do not yet carry a result.
 *
 * Read as a saved query rather than straight from the table: event_registration
 * is PRIVATE, so a signed-in athlete with no group assignment — which is every
 * athlete who simply signed up — is refused by the platform's row-level
 * security. The query carries the caller filter in its own SQL instead, the
 * same way the feed already reads registrations.
 */
export async function fetchOpenRaces(api, { signal } = {}) {
  const resp = await api.apiCall(
    `/arcee/application/${Number(envConfig.applicationId)}/analytics/${OPEN_RACES_QUERY}/run/`,
    { method: 'GET', signal },
  );
  if (!resp.ok) throw new Error('Could not load the races you have entered.');
  return asRecords(await resp.json());
}

/** "Half Marathon · Bengaluru Midnight Half" for one open-race row. */
export function openRaceLabel(row) {
  const race = (row && row.activity_name) || '';
  const event = (row && row.event_name) || '';
  if (race && event) return `${race} · ${event}`;
  return race || event || 'Your race';
}
